// src/AdminUsersPage.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { supabase } from './supabaseClient';
import { useMsal } from '@azure/msal-react';
import useDirectoryUsers from './useDirectoryUsers';

const ROLES = [
  { value: 'super_admin', label: 'Super Admin', color: 'error' },
  { value: 'admin', label: 'Admin', color: 'warning' },
  { value: 'manager', label: 'Manager', color: 'info' },
  { value: 'viewer', label: 'Viewer', color: 'default' },
];

const PERMISSIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'profiles', label: 'Profiles' },
  { key: 'closet_items', label: 'Closet Items' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'blocked_users', label: 'Blocked Users' },
  { key: 'payments', label: 'Payments' },
  { key: 'payouts', label: 'Payouts' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'chats', label: 'Chats' },
  { key: 'contact_us', label: 'Contact Us' },
  { key: 'faqs', label: 'FAQs' },
  { key: 'body_types', label: 'Body Types' },
  { key: 'brands', label: 'Brands' },
  { key: 'sizes', label: 'Sizes' },
  { key: 'style_preferences', label: 'Style Preferences' },
  { key: 'categories', label: 'Categories' },
  { key: 'subcategories', label: 'Sub Categories' },
  { key: 'issues', label: 'Issues & Errors' },
  { key: 'activity_logs', label: 'Activity Logs' },
  { key: 'system_settings', label: 'System Settings' },
  { key: 'user_management', label: 'User Management' },
];

export default function AdminUsersPage() {
  const { accounts } = useMsal();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    microsoft_email: '',
    first_name: '',
    last_name: '',
    role: 'viewer',
    permissions: {},
    active: true,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // Loaded on demand when the dialog opens, not on page load — it costs a
  // Graph call and a consent prompt, and most visits here are to look at the
  // list rather than add to it.
  const directory = useDirectoryUsers();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      console.error('Error fetching admin users:', err);
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        microsoft_email: user.microsoft_email,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        role: user.role,
        permissions: user.permissions || {},
        active: user.active !== false,
      });
    } else {
      setEditingUser(null);
      setFormData({
        microsoft_email: '',
        first_name: '',
        last_name: '',
        role: 'viewer',
        permissions: {},
        active: true,
      });
      // Only when adding. Editing an existing admin leaves the address alone,
      // so there is nothing to pick from.
      directory.load();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
  };

  const handlePermissionChange = (permission, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      if (!accounts?.[0]) {
        throw new Error('Not authenticated');
      }

      if (editingUser) {
        // Update existing user
        const { error } = await supabase
          .from('admin_users')
          .update({
            first_name: formData.first_name,
            last_name: formData.last_name,
            role: formData.role,
            permissions: formData.permissions,
            active: formData.active,
          })
          .eq('id', editingUser.id);

        if (error) throw error;
        setSnackbar({ open: true, message: 'User updated successfully!', severity: 'success' });
      } else {
        // Create new user
        const { error } = await supabase
          .from('admin_users')
          .insert([{
            microsoft_email: formData.microsoft_email,
            first_name: formData.first_name,
            last_name: formData.last_name,
            role: formData.role,
            permissions: formData.permissions,
            active: formData.active,
          }]);

        if (error) throw error;
        setSnackbar({ open: true, message: 'User created successfully!', severity: 'success' });
      }

      handleCloseDialog();
      fetchUsers();
    } catch (err) {
      console.error('Error saving user:', err);
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('id', userId);

      if (error) throw error;
      setSnackbar({ open: true, message: 'User deleted successfully!', severity: 'success' });
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    }
  };

  const getRoleChip = (role) => {
    const roleInfo = ROLES.find(r => r.value === role) || ROLES[3];
    return <Chip label={roleInfo.label} color={roleInfo.color} size="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Admin User Management</Typography>
        <Box>
          <IconButton onClick={fetchUsers} sx={{ mr: 1 }}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.microsoft_email}</TableCell>
                    <TableCell>
                      {user.first_name} {user.last_name}
                    </TableCell>
                    <TableCell>{getRoleChip(user.role)}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.active ? 'Active' : 'Inactive'}
                        color={user.active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.last_login
                        ? new Date(user.last_login).toLocaleDateString()
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(user)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(user.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit Admin User' : 'Create Admin User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {/*
                  Pick a colleague rather than typing their address. The email
                  is what is_active_admin() matches the signed-in JWT against,
                  so a typo here creates a row that can never grant anyone
                  access and gives no clue why.

                  freeSolo on purpose: listing the directory needs
                  User.ReadBasic.All, which many tenants make an administrator
                  consent to. Until someone does, `directory.error` is set, the
                  list is empty, and this still behaves as the text box it was.
                */}
                <Autocomplete
                  freeSolo
                  disabled={!!editingUser}
                  options={directory.users}
                  loading={directory.loading}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.email
                  }
                  isOptionEqualToValue={(option, value) => option.email === value.email}
                  filterOptions={(options, { inputValue }) => {
                    const q = inputValue.trim().toLowerCase();
                    if (!q) return options.slice(0, 50);
                    return options
                      .filter(o => o.name.toLowerCase().includes(q) || o.email.includes(q))
                      .slice(0, 50);
                  }}
                  value={formData.microsoft_email}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                      setFormData(prev => ({ ...prev, microsoft_email: value }));
                    }
                  }}
                  onChange={(_, option) => {
                    if (!option) return;
                    if (typeof option === 'string') {
                      setFormData(prev => ({ ...prev, microsoft_email: option }));
                      return;
                    }
                    // Split the display name so the admin does not retype what
                    // the directory already knows. Everything after the first
                    // word is the surname, which keeps double-barrelled names
                    // intact.
                    const parts = (option.name || '').trim().split(/\s+/);
                    setFormData(prev => ({
                      ...prev,
                      microsoft_email: option.email,
                      first_name: prev.first_name || parts[0] || '',
                      last_name: prev.last_name || parts.slice(1).join(' ') || '',
                    }));
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                          {option.jobTitle ? ` · ${option.jobTitle}` : ''}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Microsoft Email"
                      required
                      helperText={
                        directory.loading
                          ? 'Loading your Microsoft directory…'
                          : directory.error
                          ? // The real reason, not "something went wrong". The
                            // server tells apart a missing Graph permission
                            // from bad credentials, and either is something
                            // Samantha can act on.
                            `Directory unavailable — type the address instead. ${directory.error.message}`
                          : directory.users.length
                          ? `Start typing a name, or enter an address. ${directory.users.length} people found.`
                          : 'Type the address.'
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    label="Role"
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    {ROLES.map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Permissions
                </Typography>
                <Grid container spacing={2}>
                  {PERMISSIONS.map((perm) => (
                    <Grid item xs={12} sm={6} md={4} key={perm.key}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.permissions[perm.key] === true}
                            onChange={(e) => handlePermissionChange(perm.key, e.target.checked)}
                          />
                        }
                        label={perm.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

